import { useEffect, useState, createContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthUser, OutsetaAuthContextType } from "../types/auth";
import {
  setAccessToken,
  setAuthType,
  setTenantId,
} from "../redux/slices/authSlice";
import { useDispatch } from "react-redux";

import {
  login as msalAuth,
  initialize as msalInit,
  signOut as msalSignOut,
} from "../components/auth/msalAuth";
import { useGetSubscriptionInfoQuery } from "../redux/slices/indexApiSlice";
import { differenceInMinutes } from "date-fns";

/*
 * Setting up a auth context to be used globally
 *
 */

export const AuthContext = createContext<OutsetaAuthContextType>(
  {} as OutsetaAuthContextType
);

// export function useAuth() {
//   return useContext(AuthContext);
// }

// Outseta is added to the window by the
// Outseta script added to the head in ../public/index.html
function getOutseta() {
  if ((window as any)["Outseta"]) {
    return (window as any)["Outseta"];
  } else {
    throw new Error("Outseta is missing, have you added the script to head?");
  }
}

export default function AuthProvider({ children }: any) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState("init");
  const [user, setUser] = useState<AuthUser>({
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    displayName: "",
    avatar: "",
    role: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<"Outseta" | "MSAL">();

  const dispatchToStore = useDispatch(); // Get the dispatch function from react-redux

  const {
    data: subscriptionInfo,
    isLoading,
    isSuccess,
    isError,
    error: subscriptionErrorMessage,
  } = useGetSubscriptionInfoQuery(undefined, {
    skip: !user?.id || method !== "MSAL",
  });

  // Save a reference to Outseta
  const outsetaRef = useRef(getOutseta());

  useEffect(() => {
    // Set up handling of user related events
    handleOutsetaUserEvents(updateUser);

    if (outsetaRef.current.getAccessToken()) {
      // Outseta initalized with an authenticated user.
      updateUser();
    } else {
      // Outseta initalized without authenticated user.
      setStatus("ready");
    }

    return () => {
      // Clean up user related event subscriptions
      handleOutsetaUserEvents(() => {});
    };
  }, []);

  useEffect(() => {
    // // Set up handling of user related events
    // handleOutsetaUserEvents(updateUser);

    // Get the access token from the callback url
    const accessToken = searchParams.get("access_token");

    if (accessToken) {
      // If there is an acccess token present
      // pass it along to Outseta
      outsetaRef.current.setAccessToken(accessToken);

      // and clean up
      setSearchParams({});

      // Update the user state
      updateUser();
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    // Initialize MSAL if the user is not authenticated
    if (!user?.id) {
      initializeMSAL();
    }
  }, []);

  useEffect(() => {
    if (method === "MSAL" && isSuccess) {
      // If MSAL is used and subscription info is successfully fetched
      setUser((prevUser) => ({
        ...prevUser,
        account: subscriptionInfo, // Update the account info with the fetched subscription info
      }));
    }
  }, [isSuccess, subscriptionInfo, method]);

  const updateUser = async () => {
    // Fetch the current user data from outseta
    const outsetaUser = await outsetaRef.current.getUser();
    const accessToken = await outsetaRef.current.getAccessToken();

    dispatchToStore(setAccessToken(accessToken)); // Dispatch the action with the access to
    // dispatchToStore(setTenantId(outsetaUser.Account.Uid)); // Dispatch the action with the tenant ID
    dispatchToStore(setAuthType("Outseta")); // Set the auth type to Outseta

    if (accessToken) {
      setAccessToken(accessToken);
      setMethod("Outseta");

      let contractsLimit = 0;
      if (outsetaUser.Account.AccountStage === 2) {
        contractsLimit = 10; // Trialing accounts have a limit of 10 contracts
      } else {
        // based on the plan, set the contracts limit
        if (outsetaUser.Account.CurrentSubscription) {
          console.log(outsetaUser.Account.CurrentSubscription);
        }
      }

      // Update user state
      setUser((prevUser) => ({
        ...prevUser,
        id: outsetaUser.Uid,
        email: outsetaUser.Email,
        displayName: outsetaUser.FullName,
        firstName: outsetaUser.FirstName,
        lastName: outsetaUser.LastName,
        avatar: outsetaUser.ProfileImageS3Url,
        role: "user",
        plan: outsetaUser.Account?.CurrentSubscription?.Plan?.Name,
        isTeamManager:
          outsetaUser?.Account?.PrimaryContact?.Uid === outsetaUser.Uid,
        isFirstLogin:
          !outsetaUser.HasLoggedIn ||
          !outsetaUser.LastLoginDateTime ||
          differenceInMinutes(
            new Date(outsetaUser.LastLoginDateTime),
            new Date()
          ) < 5,
        account: {
          id: outsetaUser.Account.Uid,
          name: outsetaUser.Account.Name,
          logo: outsetaUser.Account.LogoUrl, // Use the custom LogoUrl field
          isActive:
            outsetaUser.Account.CurrentSubscription ||
            outsetaUser.Account.AccountStage === 2
              ? true
              : false,
          spykioIndex: outsetaUser.Account.SpykioIndexName,
          tenantId: outsetaUser.Account.Microsoft365TenantId,
          contractsLimit,
          contractsAdded: outsetaUser.Account.ContractsAdded,
          contractsRemaining:
            outsetaUser.Account.ContractsAdded > 0
              ? contractsLimit - outsetaUser.Account.ContractsAdded
              : contractsLimit,
          chatbotMessagesLimit: 10, // Needs to be updated based on the plan
          promptsLibraryLimit: 5, // Needs to be updated based on the plan
        },
      }));

      // Make sure status = ready
      setStatus("ready");
    }
  };

  const handleOutsetaUserEvents = (onEvent: any) => {
    // Subscribe to user related events
    // with onEvent function
    const outseta = outsetaRef.current;
    outseta.on("subscription.update", onEvent);
    outseta.on("profile.update", onEvent);
    outseta.on("account.update", onEvent);
  };

  const logout = () => {
    // Unset access token
    outsetaRef.current.setAccessToken("");

    // if msal is initialized, logout from MSAL
    if (method === "MSAL") {
      msalSignOut();
    }

    // and remove user state
    setUser({
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      displayName: "",
      avatar: "",
      role: "",
    });

    setMethod(undefined);
    setStatus("");
  };

  const openLogin = (options: any) => {
    outsetaRef.current.auth.open({
      widgetMode: "login|register",
      //   authenticationCallbackUrl: window.location.href,
      ...options,
    });
  };

  const openSignup = (options: any) => {
    outsetaRef.current.auth.open({
      widgetMode: "register",
      authenticationCallbackUrl: window.location.href,
      ...options,
    });
  };

  const openProfile = (options: any) => {
    outsetaRef.current.profile.open({ tab: "profile", ...options });
  };

  const initializeMSAL = async () => {
    msalInit().then(async (authenticationResult) => {
      const isAuthenticated = !!(
        authenticationResult && authenticationResult.accessToken !== null
      );
      let user = null;

      if (authenticationResult && isAuthenticated) {
        dispatchToStore(setAccessToken(authenticationResult.accessToken)); // Dispatch the action with the access token
        dispatchToStore(setTenantId(authenticationResult.tenantId)); // Dispatch the action with the access token
        setMethod("MSAL");
        // get the profile picture from the Microsoft Graph API
        //  await getUserPhoto(authenticationResult.uniqueId);

        user = {
          id: authenticationResult.uniqueId,
          avatar: null, // photoSuccess ? userPhoto : null, // state?.user?.picture,
          email: authenticationResult.account?.username,
          displayName: authenticationResult.account?.name,
          role: "user",
        };

        setUser({
          id: authenticationResult.uniqueId,
          email: authenticationResult.account?.username || "",
          displayName: authenticationResult.account?.name || "",
          firstName: authenticationResult.account?.name || "",
          lastName: authenticationResult.account?.name || "",
          avatar: null, // photoSuccess ? userPhoto : null,
          account: subscriptionInfo, // this is the Outseta account
          role: "user",
        });

        // Make sure status = ready
        setStatus("ready");
      } else {
        // setStatus("ready");
      }
    });
  };

  const msalLogin = async () => {
    setError(null);
    setMethod("MSAL");
    const authenticationResult = await msalAuth();
    const isAuthenticated = authenticationResult.accessToken !== null;

    if (authenticationResult && isAuthenticated) {
      dispatchToStore(setAccessToken(authenticationResult.accessToken)); // Dispatch the action with the access token
      dispatchToStore(setTenantId(authenticationResult.tenantId)); // Dispatch the action with the access token
      // get the profile picture from the Microsoft Graph API
      // await getUserPhoto(authenticationResult.uniqueId);

      setUser({
        id: authenticationResult.uniqueId,
        email: authenticationResult.account?.username || "",
        displayName: authenticationResult.account?.name || "",
        firstName: authenticationResult.account?.name || "",
        lastName: authenticationResult.account?.name || "",
        avatar: authenticationResult.account?.name || "",
        account: subscriptionInfo, // this is the Outseta account
        role: "user",
      });

      // Make sure status = ready
      setStatus("ready");
    } else {
      setUser({
        id: "",
        email: "",
        firstName: "",
      });

      setError("Authentication failed. Please try again.");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        method,
        isAuthenticated: user?.id && user?.account ? true : false,
        isInitialized: status === "ready",
        user,
        isLoading: status !== "ready",
        logout,
        openLogin,
        openSignup,
        openProfile,
        msalLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
